import type { Color } from "@core/colors";

/**
 * Rubber Rings 3D — hand-authored levels.
 *
 * The play "model" is a rotating cube; rings are placed on its 6 faces.
 * Each ring is described by its color, the face it sits on, and (u, v)
 * coordinates within the face (range roughly [-0.95, 0.95] — see main.ts
 * `facePoint`).
 *
 * `palette` is the set of box colors available at the bottom of the screen.
 * Each color in `rings` MUST appear in `palette` AND the count of rings per
 * color must be a multiple of 3 (BOX_CAPACITY = 3) so every collected
 * ring has a destination.
 *
 * Progression: 6 rings (2 colors) → 12 rings (4 colors) by L5. Higher
 * levels rotate rings onto more faces so the player has to spin the cube.
 */
export type CubeFace = "x+" | "x-" | "y+" | "y-" | "z+" | "z-";

export interface RingSpec {
  color: Color;
  face: CubeFace;
  /** Position across the face, ~[-0.95, 0.95]. */
  u: number;
  v: number;
}

export interface RingsLevel {
  palette: Color[];
  rings: RingSpec[];
}

// L1 — 6 rings, 2 colors, all on the +z face for an obvious "rotate not
// required" first puzzle.
const L1: RingsLevel = {
  palette: ["red", "blue"],
  rings: [
    { color: "red", face: "z+", u: -0.6, v: 0.5 },
    { color: "red", face: "z+", u: 0.6, v: 0.5 },
    { color: "red", face: "z+", u: 0.0, v: 0.0 },
    { color: "blue", face: "z+", u: -0.6, v: -0.5 },
    { color: "blue", face: "z+", u: 0.6, v: -0.5 },
    { color: "blue", face: "z+", u: 0.0, v: -0.8 },
  ],
};

// L2 — 6 rings, 2 colors, spread over 2 faces (+z and +y). Light swipe to
// reach the top face.
const L2: RingsLevel = {
  palette: ["red", "blue"],
  rings: [
    { color: "red", face: "z+", u: -0.6, v: 0.5 },
    { color: "red", face: "z+", u: 0.6, v: 0.5 },
    { color: "red", face: "y+", u: 0.0, v: 0.0 },
    { color: "blue", face: "z+", u: -0.6, v: -0.5 },
    { color: "blue", face: "z+", u: 0.6, v: -0.5 },
    { color: "blue", face: "y+", u: 0.5, v: 0.5 },
  ],
};

// L3 — 9 rings, 3 colors, three faces.
const L3: RingsLevel = {
  palette: ["red", "blue", "yellow"],
  rings: [
    { color: "red", face: "z+", u: -0.6, v: 0.5 },
    { color: "red", face: "z+", u: 0.6, v: 0.4 },
    { color: "red", face: "y+", u: 0.5, v: 0.5 },

    { color: "blue", face: "z+", u: -0.5, v: -0.5 },
    { color: "blue", face: "x+", u: 0.4, v: 0.4 },
    { color: "blue", face: "y-", u: -0.4, v: 0.0 },

    { color: "yellow", face: "z+", u: 0.6, v: -0.5 },
    { color: "yellow", face: "x-", u: 0.0, v: -0.4 },
    { color: "yellow", face: "y+", u: -0.5, v: -0.4 },
  ],
};

// L4 — 9 rings, 3 colors, all 6 faces touched. Need to spin to see all.
const L4: RingsLevel = {
  palette: ["red", "blue", "green"],
  rings: [
    { color: "red", face: "z+", u: -0.5, v: 0.5 },
    { color: "red", face: "x+", u: 0.0, v: 0.0 },
    { color: "red", face: "z-", u: 0.5, v: -0.5 },

    { color: "blue", face: "z+", u: 0.5, v: -0.5 },
    { color: "blue", face: "x-", u: 0.0, v: 0.4 },
    { color: "blue", face: "y+", u: -0.5, v: 0.5 },

    { color: "green", face: "y-", u: 0.0, v: 0.0 },
    { color: "green", face: "x+", u: -0.5, v: -0.5 },
    { color: "green", face: "z-", u: -0.5, v: 0.5 },
  ],
};

// L5 — 12 rings, 4 colors, dense layout across all faces. Designed to
// require thought (which color to fully drain first) but still always
// solvable: there are exactly 3 of each color and 4 boxes available, so
// every ring has a target box at every moment.
const L5: RingsLevel = {
  palette: ["red", "blue", "yellow", "green"],
  rings: [
    { color: "red", face: "z+", u: -0.6, v: 0.5 },
    { color: "red", face: "x+", u: 0.5, v: 0.0 },
    { color: "red", face: "y-", u: -0.4, v: 0.4 },

    { color: "blue", face: "z+", u: 0.6, v: 0.5 },
    { color: "blue", face: "x-", u: -0.5, v: 0.0 },
    { color: "blue", face: "y+", u: 0.4, v: -0.4 },

    { color: "yellow", face: "z+", u: -0.6, v: -0.5 },
    { color: "yellow", face: "x+", u: -0.4, v: -0.4 },
    { color: "yellow", face: "z-", u: 0.5, v: 0.5 },

    { color: "green", face: "z+", u: 0.6, v: -0.5 },
    { color: "green", face: "y-", u: 0.4, v: -0.4 },
    { color: "green", face: "z-", u: -0.5, v: -0.5 },
  ],
};

export const levels: RingsLevel[] = [L1, L2, L3, L4, L5];
